import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'

import {
  Company,
  CompanyLicenseInfo,
  CompanyMember,
  CompanyPreferencesResponse,
  CustomPreference,
  CreateCompanyParams,
  SamlMappingUploadResult,
  UpdateCompanyParams
} from 'common/types/company'
import GenericPaginationParams from 'common/types/genericPaginationParams'
import { PaginatedResponse } from 'common/types/managers'
import { LanguageValues } from 'common/types/instance'

const ENDPOINT = '/companies'

interface GenericBooleanResponse {
  result: boolean
}

export class CompanyApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    Object.setPrototypeOf(this, CompanyApi.prototype)
  }

  public async getAllCompanies(
    params?: GenericPaginationParams
  ): Promise<PaginatedResponse<Company>> {
    try {
      const res: AxiosResponse<PaginatedResponse<Company>> = await this.get<
        Company,
        AxiosResponse<PaginatedResponse<Company>>
      >(`${ENDPOINT}/`, { params: { ...params } })

      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getCompanyById(id: string): Promise<Company> {
    try {
      const res: AxiosResponse<Company> = await this.get(`${ENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getLicenseExpirationRecipientOptions(id: string): Promise<CompanyMember[]> {
    try {
      const res: AxiosResponse<CompanyMember[]> = await this.get(
        `${ENDPOINT}/${id}/license_expiration_recipient_options/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  /** The license info of the company with `licence` in place of its own, without saving it. */
  public async previewLicense(id: number, licence: string): Promise<CompanyLicenseInfo> {
    try {
      const res: AxiosResponse<CompanyLicenseInfo> = await this.post(
        `${ENDPOINT}/${id}/license_preview/`,
        { licence }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getFullCompanyById(id: string): Promise<Company> {
    try {
      const res: AxiosResponse<Company> = await this.get(
        `${ENDPOINT}/get_full_company/?company_id=${id}`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getCompanyPreferencesByCompanyId(id: string): Promise<CompanyPreferencesResponse> {
    try {
      const res: AxiosResponse<CompanyPreferencesResponse> = await this.get(
        `${ENDPOINT}/${id}/preferences/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  /**
   * Toggles Company Active status.
   *
   * @param {number} id
   * @returns {"result":boolean}
   */
  public async toggleCompanyActive(id: number): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.patch(
        `${ENDPOINT}/toggle_company_active/`,
        { id }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   * Permanently deletes a company's internal data (users assignments, departments,
   * teams, roles, secrets, preferences, scheduled tasks and file storage) without
   * deleting the Company row itself.
   *
   * @param {number} id
   * @returns {"result":boolean}
   */
  public async deleteCompanyData(id: number): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/delete_company_data/`,
        { id }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if Company Code (folder name) is already used
   *
   * @param {code} string
   * @returns {"result":boolean}
   */
  public async companyCodeExists(code: string): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.get(
        `${ENDPOINT}/code_exists/?code=${code}`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async createCompany(companyData: CreateCompanyParams): Promise<Company> {
    try {
      const res: AxiosResponse<Company> = await this.post(`${ENDPOINT}/`, companyData, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async createCompanyPreference(
    companyPreferenceData: CustomPreference
  ): Promise<CompanyPreferencesResponse> {
    try {
      const res: AxiosResponse<CompanyPreferencesResponse> = await this.post(
        `/companyPreferences/`,
        companyPreferenceData,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async updateCompany(
    id: string | undefined,
    companyData: UpdateCompanyParams
  ): Promise<Company> {
    try {
      const res: AxiosResponse<Company> = await this.patch(`${ENDPOINT}/${id}/`, companyData, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async updateCompanyPreference(
    id: string | number | undefined,
    companyPreferenceData: CustomPreference
  ): Promise<CompanyPreferencesResponse> {
    try {
      const res: AxiosResponse<CompanyPreferencesResponse> = await this.put(
        `/companyPreferences/${id}/`,
        companyPreferenceData,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async uploadLogo(id: number, data: FormData): Promise<string> {
    const response: AxiosResponse<{ result: string }> = await this.post(
      `${ENDPOINT}/${id}/upload_logo/`,
      data
    )
    return this.success(response).result
  }
  public async uploadEmailLogo(id: number, data: FormData): Promise<string> {
    const response: AxiosResponse<{ result: string }> = await this.post(
      `${ENDPOINT}/${id}/upload_email_logo/`,
      data
    )
    return this.success(response).result
  }
  public async uploadSamlMappingFile(
    companyPreferenceId: number | string,
    kind: 'upload_mapping_py' | 'upload_metadata_xml',
    data: FormData
  ): Promise<SamlMappingUploadResult> {
    const res: AxiosResponse<SamlMappingUploadResult> = await this.post(
      `/companyPreferences/${companyPreferenceId}/${kind}/`,
      data
    )
    return this.success(res)
  }
  public async deleteCompanyPreference(
    id: string | number | undefined
  ): Promise<CompanyPreferencesResponse> {
    try {
      const res: AxiosResponse<CompanyPreferencesResponse> = await this.delete(
        `/companyPreferences/${id}/`,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async setLanguagePreference(data: { language: LanguageValues }): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(
        `userCompanyPreferences/setLanguagePreference/`,
        data
      )
      return this.success<void>(res)
    } catch (error) {
      throw error
    }
  }
  public async setSelfServicePreference(data: { code: string; value: string }): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(
        `userCompanyPreferences/setSelfServicePreference/`,
        data
      )
      return this.success<void>(res)
    } catch (error) {
      throw error
    }
  }
  public async setNumberLocalePreference(data: { numberLocale: string }): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(
        `userCompanyPreferences/setNumberLocalePreference/`,
        data
      )
      return this.success<void>(res)
    } catch (error) {
      throw error
    }
  }
}

export const companyApi = new CompanyApi(apiConfig)
